import React, { useState, useEffect, ReactNode, useContext } from 'react';

import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import * as Sentry from '@sentry/browser';

import {
  AnalyticsContext,
  ChannelsContext,
  UserDataContext,
} from 'lane-shared/contexts';
import { emitUserLoggedIn } from 'lane-shared/helpers/analytics/emitIntegrationLogin';
import { validateBEPrismBuildingIdAgainstCurrentUser } from 'lane-shared/helpers/constants/integrations/BuildingEnginesPrismCommon';
import { FORGOT_PASSWORD_URL } from 'lane-shared/helpers/constants/integrations/BuildingEnginesPrismWorkOrders';
import * as BuildingEnginesPrism from 'lane-shared/helpers/integrations/BuildingEnginesPrism';
import {
  getToken,
  storeToken,
} from 'lane-shared/helpers/integrations/BuildingEnginesPrism/token';
import { useBEPrismManualLogin } from 'lane-shared/hooks/integrations/BuildingEnginesPrism';
import { ContentType } from 'lane-shared/types/content/Content';

import { history } from '../../../helpers';
import CardContainer from '../../cards/CardContainer';
import ValidatedInput from '../../form/ValidatedInput';
import { Button, ErrorMessage, Loading } from '../../general';
import { BuildingIntegrationFailure } from './BuildingIntegrationFailure';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { IntegrationProviderEnum } from 'constants-integrations';
import { convertToUUID } from 'uuid-encoding';
import { useBEPrismTokenContext } from 'lane-shared/contexts/BuildingEnginesPrism/useBEPrismTokenContext';

import styles from './Login.scss';
import {useBEPrismBuildingsContext} from 'lane-shared/contexts/BuildingEnginesPrism/useBEPrismBuildingsContext';
import { useGetAuthToken } from '../../../hooks/useGetAuthToken';

interface LoginProps {
  content: ContentType;
  children?: ReactNode;
}

const emailValidator = yup.string().email().nullable().required();
const pwdValidator = yup.string().nullable().required();

export default function Login({ content, children }: LoginProps) {
  const { settings } = content.integration;

  const { t } = useTranslation();

  // This should be refactored into a reducer in the future
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [validation, setValidation] = useState<Error | null>(null);
  const [storedToken, setStoredToken] = useState<string | undefined>(undefined);
  const [buildingIntegrationError, setBuildingIntegrationError] =
    useState<boolean>(false);
  const [ssoFailed, setSsoFailed] = useState<boolean>(false);

  const isBEFFEnabled = useFlag(FeatureFlag.EnableSSOAndMultiBuildingForBE, false);
  const { buildingId: selectedBuildingId, setBuildingId, setBuildings, setLoadingBuildings, loadingBuildings } = useBEPrismBuildingsContext();
  const buildingId = isBEFFEnabled ? selectedBuildingId : settings?.buildingId;
  const enableTokenManagement = useFlag(FeatureFlag.SquadTechnicalSolutionsBuildingEngineTokenManagement, false);

  const configBuildingIds = React.useMemo(() => {
    return Array.isArray(settings?.buildingIds)
      ? settings.buildingIds.map((b: any) => b.value).join(',')
      : '';
  }, [settings?.buildingIds]);

  const analytics = useContext(AnalyticsContext);
  const { user } = useContext(UserDataContext);
  const { primaryChannel } = useContext(ChannelsContext);
  const tokenContext = useBEPrismTokenContext();
  const { 
    isManualLogin, 
    setManualLoginFlag, 
    clearManualLoginFlag 
  } = useBEPrismManualLogin();

  const getAuthToken = useGetAuthToken();

  function validate() {
    try {
      emailValidator.validateSync(email);
      pwdValidator.validateSync(password);
      setValidation(null);
    } catch (err) {
      if (err instanceof Error) {
        setValidation(err);
      }
    }
  }

  async function getBuildings() {
    const response = await BuildingEnginesPrism.requestHelpers.getTxBuildingList(
      '',
      configBuildingIds,
      {
        enableTokenManagement,
        getAuthToken,
        channelIntegrationId: convertToUUID(content?.integration?._id),
      }
    );

    if (response.isError || !('results' in response) || !response.results?.length) {
      setBuildings([]);
      setLoadingBuildings(false);

      return;
    }

    const rawBuildings = response.results.map((b: any) => ({ id: b.id, name: b.name }));

    setBuildings(rawBuildings);

    if (rawBuildings.length > 0) {
      setBuildingId(rawBuildings[0].id);
    }

    setLoadingBuildings(false);
  }

  /**
   * Once the user logs in, we check if the currentUser
   * has access to the buildingId in the integration settings
   * When access isn't available, redirects to the
   * BuildingIntegration error
   */
  async function login() {
    setLoginLoading(true);
    setError(null);

    try {

      let response;
      let isManualLoginAttempt = false;
      
      if (isBEFFEnabled && !ssoFailed) {
        response = await BuildingEnginesPrism.requestHelpers.ssoLogin(
          user?.profile?.email || '',
          settings?.tenantAccountId,
          convertToUUID(content?.integration?._id),
          {
            getAuthToken,
            enableTokenManagement,
          }
        );

        if (response.isError) {
          setLoginLoading(false);
          setSsoFailed(true);
          
          const errorMessage = t(response.error);
          
          setError(new Error(errorMessage));

          return;
        }

        if (!response.token && !enableTokenManagement) {
          setLoginLoading(false);
          setSsoFailed(true);
          setError(new Error(t('shared.integrations.bePrism.BuildingIntegrationFailed.ssologin')));

          return;
        }

        if (enableTokenManagement) {
          await getBuildings();
        }
      } else {
        isManualLoginAttempt = true;
        response = await BuildingEnginesPrism.requestHelpers.login(
          email || '',
          password || ''
        );

        if (response.isError) {
          setLoginLoading(false);
          setError(new Error(t("You've entered an invalid email or password.")));

          return;
        }

        if (!response.token) {
          setLoginLoading(false);
          setError(new Error(t("Token is required to validate building ID.")));

          return;
        }

        const currentState = history.location.state || {};

        history.replace({
          ...history.location,
          state: { ...currentState, manualLogin: true },
        });
      }

      const token = response.token;

      if (!isBEFFEnabled) {
        const isIntegrationValid: boolean =
          await validateBEPrismBuildingIdAgainstCurrentUser({
            token,
            buildingId,
          });

        if (!isIntegrationValid) {
          setBuildingIntegrationError(true);
          setLoginLoading(false);

          return;
        }
      }

      await storeToken(token);
      
      // Set manual login flag if this was a manual login attempt
      if (isManualLoginAttempt) {
        await setManualLoginFlag(true);
      } else {
        // Clear manual login flag for SSO login
        await clearManualLoginFlag();
      }

      setStoredToken(token);

      if (user && primaryChannel) {
        emitUserLoggedIn({
          userId: user._id,
          channelId: primaryChannel._id,
          integrationId: content.integration._id,
          analytics,
        });
      }

      if (children) {
        setLoginLoading(false);
      } else {
        history.goBack();
      }
    } catch (err) {
      setLoginLoading(false);
      Sentry.captureException(err, {
        contexts: {
          extraData: { contentName: content.name },
        },
      });

      if (err instanceof Error) {
        if (err.message) {
          setError(err);
        } else {
          window.Alert.alert({
            title: t('Whoops something went wrong!'),
            message: t('Please try again later.'),
          });
        }
      }
    }
  }

  async function getTokenOnLoadBEFFEnabled() {

    const existingToken = await getToken();
    const loginState = (history.location?.state as { manualLogin?: boolean })?.manualLogin;

    if (loginState && existingToken) {
      setStoredToken(existingToken);
      setLoginLoading(false);

      return;
    }

    if (ssoFailed) {
      setLoginLoading(false);

      return;
    }

    await login();
  }

  async function getTokenOnLoadNotBEFFEnabled() {
    setStoredToken(await getToken());
    setLoginLoading(false);
  }

  const getTokenForRequest = async (): Promise<string | null | undefined> => {

    if (isBEFFEnabled && tokenContext?.getTxToken && !isManualLogin) {
      const tokenResult = await tokenContext.getTxToken();

      return tokenResult.token;
    }

    return storedToken;
  };

  useEffect(() => {
    if (isBEFFEnabled) {
      setSsoFailed(false);
      getTokenOnLoadBEFFEnabled();
    } else {
      getTokenOnLoadNotBEFFEnabled();
    }
  }, [isBEFFEnabled,settings?.tenantAccountId]);

  useEffect(() => {
    if (isBEFFEnabled) {
      if (ssoFailed && (email !== null || password !== null)) {
        validate();
      }
    } else if (email !== null || password !== null) {
        validate();
      }
  }, [email, password, ssoFailed, isBEFFEnabled]);

  useEffect(() => {
    if (isBEFFEnabled && storedToken !== undefined && (!enableTokenManagement || isManualLogin)) {
      setLoadingBuildings(true);
      (async () => {
        try {
          if (!configBuildingIds) {
            setLoadingBuildings(false);

            return;
          }

          const token = await getTokenForRequest();

          if (!token) {
            setLoadingBuildings(false);

            return;
          }

          const response = await BuildingEnginesPrism.requestHelpers.getTxBuildingList(token,configBuildingIds);

          if (response.isError || !('results' in response) || !response.results?.length) {
            setBuildings([]);
            setLoadingBuildings(false);

            return;
          }

          const rawBuildings = response.results.map((b: any) => ({ id: b.id, name: b.name }));

          setBuildings(rawBuildings);

          if (rawBuildings.length > 0) {
            setBuildingId(rawBuildings[0].id);
          }

          setLoadingBuildings(false);
        } catch {
          setBuildings([]);
          setLoadingBuildings(false);
        }
      })();
    }
  }, [isBEFFEnabled, storedToken, configBuildingIds, setLoadingBuildings, setBuildings, setBuildingId, isManualLogin, enableTokenManagement]);

  if (storedToken && children) {
    return <>{children}</>;
  }

  if (buildingIntegrationError) {
    return (
      <BuildingIntegrationFailure
        setBuildingIntegrationError={setBuildingIntegrationError}
      />
    );
  }

  if (loginLoading || (isBEFFEnabled && loadingBuildings)) {
    return <Loading />;
  }

  if (enableTokenManagement && !ssoFailed && !isManualLogin && children) {
    return <>{children}</>;
  }

  return (
    <CardContainer size="large" className={styles.container}>
      {!isBEFFEnabled && <h2>{t('Log in')}</h2>}
      <p>
        {isBEFFEnabled
          ? t(
              content?.integration?.integration?.name === IntegrationProviderEnum.BuildingEnginesPrism
                ? 'shared.integrations.bePrism.login.workOrder'
                : 'shared.integrations.bePrism.login.visitor'
            )
          : t(
              'Log in with your {{name}} account to send and receive maintenance requests',
              { name: settings.name }
            )}
      </p>
      {(!isBEFFEnabled || ssoFailed) && (
        <form autoComplete="off">
          <ValidatedInput
            type="email"
            placeholder={
              isBEFFEnabled
                ? t('shared.integrations.bePrism.login.email')
                : t('Enter your {{name}} email', { name: settings.name })
            }
            onChange={value => {
              setEmail(value);
            }}
            label={t('Email')}
            value={email}
            validation={emailValidator}
            isPristine={typeof email === 'object'}
          />
          <ValidatedInput
            type="password"
            placeholder={
              isBEFFEnabled
                ? t('shared.integrations.bePrism.login.password')
                : t('Enter your {{name}} password', { name: settings.name })
            }
            onChange={value => {
              setPassword(value);
            }}
            label={t('Password')}
            value={password}
            validation={pwdValidator}
            isPristine={typeof password === 'object'}
          />

          <a href={FORGOT_PASSWORD_URL} target="_blank" rel="noopener noreferrer">
            {t('Forgot password?')}
          </a>
          <Button
            disabled={Boolean(validation) || !email || !password}
            className={styles.button}
            variant="contained"
            testId="BEloginButton"
            onClick={login}
          >
            {t('Log in')}
          </Button>
        </form>
      )}
      {settings.supportEmail && (
        <p>
          Don&apos;t have a {settings.name} account?{' '}
          <a href={`mailto:${settings.supportEmail}`}>
            {t('Contact your administrator.')}
          </a>
        </p>
      )}
      {error && <ErrorMessage error={error} />}
    </CardContainer>
  );
}